import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Filter } from '~commonInfra/utils/Filter';
import { usePrevious } from '~src/bootstrap/hooks';
import { setPreviousUrl } from '~src/bootstrap/redux/actions/SetPreviousUrlReducerAction';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '~src/bootstrap/routerHelpers/QueryParamUpdateProvider';
import { RouterError } from '~src/bootstrap/routerHelpers/RouterError';
import { BrowserTitleService } from '~src/commonInfra/services/BrowserTitleService';
import { addContentFavorite, removeContentFavorite } from '~src/favorites/actions/FavoritesActions';
import { Projects, ProjectsResource } from '~src/projects/resources/Projects';
import { RequestAccessAction } from '~src/requestAccess/actions/RequestAccessAction';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { enterSpace } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { openContentDetailDialog } from '~src/spacesPlatform/actions/OpenContentDetailDialogActions';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { IFavoritableItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { IProjectInfo } from '../models/IProjectInfo';
import { IProjectActions } from '../ServerApi/types/IProjectActions';
import { ProjectQueryProvider } from '../services/Queries';
import { ConnectedProjectSpace } from './ConnectedProjectSpace';
import { ProjectSpaceOwnProps } from './ProjectSpace';


export interface ProjectSpaceProviderOwnProps {
  readonly routeLocationSearch: string;
  readonly siteName: string;
  readonly projectId: string;
  readonly urls: SpacesSiteUrls;
  readonly grantAccessDisplay: boolean;
  readonly grantAccessUrl: string;
  readonly renderProjectSpace: (props: ProjectSpaceOwnProps) => JSX.Element;
  readonly setFetchedAllData: (isFetched: boolean) => void;
}

export interface ProjectSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly openContentDetailDialog: (content: IContentInfo, availableActions: IProjectActions) => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
}

export interface ProjectSpaceProviderStateProps {
  readonly isRequestAccessEnabled: boolean;
}

export interface ProjectSpaceProviderPropsFromServices {
  readonly Projects: ProjectsResource;
}

export type ProjectSpaceNonServicesProps = ProjectSpaceProviderStateProps & ProjectSpaceProviderOwnProps & ProjectSpaceProviderDispatchProps;

export type ProjectSpaceProviderContainerProps =  ProjectSpaceProviderStateProps & ProjectSpaceProviderOwnProps &  ProjectSpaceProviderDispatchProps & ProjectSpaceProviderPropsFromServices;

export interface ProjectSpaceProviderContainerState {
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
}

const InitialState: ProjectSpaceProviderContainerState = {
  initialRender: true,
  renderChildren: false
};

export const ProjectSpaceProviderContainer: React.FunctionComponent<ProjectSpaceProviderContainerProps> = React.memo((props) => {
  const [state, setState] = React.useState<ProjectSpaceProviderContainerState>(InitialState);
  const unmountedRef = React.useRef<boolean>(false);

  const {
    projectId,
    urls,
    setFetchedAllData,
    openContentDetailDialog,
    addContentFavorite,
    removeContentFavorite,
    enterSpace,
    grantAccessDisplay,
    grantAccessUrl,
    requestSpaceContents,
    isRequestAccessEnabled,
    renderProjectSpace,
    setPreviousUrl,
    siteName,
  } = props;

  const previousProjectId = usePrevious(props.projectId);
  const previousSiteName = usePrevious(props.siteName);

  const resetPreviousUrl = React.useCallback(() => {
    setPreviousUrl(window.location.href);
  }, [setPreviousUrl]);

  const projectInfoQuery = useQuery({
    queryKey: ['projectInfo', projectId],
    queryFn: async () => {
      const project = await props.Projects.all()
        .withFilters([Filter.fieldEq('id', projectId)])
        .getFirst();
      return project as IProjectInfo | null;
    },
    enabled: !!projectId,
    refetchOnWindowFocus: false
  });
  const projectInfo = projectInfoQuery.data;

  const projectDetailQuery = useQuery({
    queryKey: ['projectDetail', projectId],
    queryFn: async () => {
      const detail = await props.Projects.getById(projectId);
      const projectHierarchy = await props.Projects.getProjectHierarchy(detail.id);
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);

      populateContentBreadcrumbs(projectHierarchyWithType, urls);

      return {
        ...detail,
        hierarchy: projectHierarchyWithType
      };
    },
    enabled: !!projectId,
    refetchOnWindowFocus: false
  });
  const projectDetail = projectDetailQuery.data;

  const projectActionsQuery = useQuery({
    queryKey: ['projectActions', projectId],
    queryFn: async () => {
      const actions = await props.Projects.actions(projectId);
      return actions as IProjectActions | null;
    },
    enabled: !!projectId,
    refetchOnWindowFocus: false
  });
  const projectActions = projectActionsQuery.data;
  const haveAllDataNeededToRender = projectInfo != null && projectDetail != null && projectActions != null;

  const queriesFetched = projectInfoQuery.isFetched && projectDetailQuery.isFetched && projectActionsQuery.isFetched;

  const getProjectDataState = React.useCallback(() => {
    const errorObject = {
      hasError: false,
      errorCode: undefined as number | undefined,
      projectInfo: projectInfo ?? undefined,
      projectDetail: projectDetail ?? undefined,
      projectActions: projectActions ?? undefined
    };

    if (projectInfoQuery.isError || projectDetailQuery.isError || projectActionsQuery.isError) {
      errorObject.hasError = true;

      const errorCode = projectDetailQuery.error ?? projectInfoQuery.error ?? projectActionsQuery.error;

      // the underlying promises error by returning a number (error code),
      // so we can rely on type narrowing
      if (typeof errorCode === 'number') {
        errorObject.errorCode = errorCode;
      }
    }

    if (queriesFetched && !haveAllDataNeededToRender) {
      errorObject.hasError = true;
    }

    return errorObject;
  }, [projectInfoQuery, projectDetailQuery, projectActionsQuery, queriesFetched, haveAllDataNeededToRender, projectInfo, projectDetail, projectActions]);

  const fetchState = getProjectDataState();
  const wrappedOpenContentDetailDialog = React.useCallback(() => {
    openContentDetailDialog(projectDetail, projectActions);
  }, [openContentDetailDialog, projectDetail, projectActions]);

  const favorite = React.useCallback(() => {
    addContentFavorite(projectDetail);
  }, [addContentFavorite, projectDetail]);

  const unfavorite = React.useCallback(() => {
    removeContentFavorite(projectDetail);
  }, [removeContentFavorite, projectDetail]);

  React.useEffect(() => {
    return () => { unmountedRef.current = true; };
  }, []);

  React.useEffect(() => {
    const hasPrevious = previousSiteName !== undefined && previousProjectId !== undefined;
    const inputsDefined = siteName !== undefined && projectId !== undefined;
    if (hasPrevious && inputsDefined && (previousSiteName !== siteName || previousProjectId !== projectId)) {
      setFetchedAllData(false);
      setState(InitialState);
    }
  }, [siteName, projectId, previousSiteName, previousProjectId, setFetchedAllData]);

  React.useEffect(() => {
    if (!haveAllDataNeededToRender || fetchState.hasError) {
      return;
    }

    if (state.initialRender) {
      enterSpace({
        item: projectDetail,
        spaceType: 'project',
        subspaceName: undefined
      });

      setState(prev => ({ ...prev, initialRender: false, renderChildren: true }));

      if (grantAccessDisplay) {
        RequestAccessAction.handleGrantAccess(projectDetail, grantAccessUrl);
        resetPreviousUrl();
      }

      requestSpaceContents();

      setState(prev => ({ ...prev, initialRender: false, renderChildren: true }));

      BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: projectDetail.name });
      setFetchedAllData(true);
    }
  }, [haveAllDataNeededToRender, fetchState.hasError, state.initialRender, projectDetail, enterSpace, grantAccessDisplay, grantAccessUrl, requestSpaceContents, resetPreviousUrl, setFetchedAllData]);

  if (state.renderChildren && haveAllDataNeededToRender) {
    return renderProjectSpace({
      projectDetail: {
        usageInfo: projectInfo!.usageInfo,
        usageInfoMaxValues: projectInfo!.usageInfoMaxValues,
        ...projectDetail!
      },
      projectId: projectDetail!.id,
      openDetail: wrappedOpenContentDetailDialog,
      addToFavorites: favorite,
      removeFromFavorites: unfavorite
    });
  } else if (fetchState.hasError) {
    if (isRequestAccessEnabled) {
      // Redirect to request-access page if:
      // 1. Error code explicitly indicates insufficient permissions, OR
      // 2. projectInfo is null but projectDetail exists (inferred insufficient permissions - TFS954605)
      const shouldRedirect =
        fetchState.errorCode === CommonErrorCode.CallingUserInsufficientPermissions ||
        (!fetchState.errorCode && fetchState.projectInfo === null && !_.isUndefined(fetchState.projectDetail));

      if (shouldRedirect) {
        return <Redirect to={`${urls.homePath()}${urls.projectRequestAccessPath(projectId)}`} />;
      }

      return <RequestAccessError url={urls.exploreUrl()} contentIdentifier={projectId} contentType={'project'} errorCode={fetchState.errorCode}/>;
    }
    return <RouterError errorType={'projects'} urls={urls} />;
  } else {
    return null;
  }
});

const mapStateToProps: MapStateToProps<ProjectSpaceProviderStateProps> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<ProjectSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  requestSpaceContents,
  enterSpace,
  addContentFavorite,
  removeContentFavorite,
  openContentDetailDialog,
  setPreviousUrl,
}, dispatch);

function ProjectSpaceServiceProvider(props: ProjectSpaceNonServicesProps) {
  return (<ProjectSpaceProviderContainer
    {...props}
    Projects={Projects}
  />);
}

export const ConnectedProjectSpaceProviderContainer: React.ComponentClass<ProjectSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(ProjectSpaceServiceProvider);

const renderProjectSpace = (props: ProjectSpaceOwnProps): JSX.Element => <ConnectedProjectSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}

export type ProjectSpaceProviderProps = LocationPathProp & Omit<ProjectSpaceProviderOwnProps, 'renderProjectSpace' | 'setFetchedAllData'>;

export interface ProjectSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export const ProjectSpaceProvider: React.FunctionComponent<ProjectSpaceProviderProps> = React.memo(props => {

  const [fetchedAllData, setFetchedAllData] = React.useState(false);
  const previousProjectId = usePrevious(props.projectId);

  // TFSID:983648.  Handle case when updating this component with a new projectId
  const resetStore = !_.isUndefined(previousProjectId) && !_.isUndefined(props.projectId)
    && previousProjectId !== props.projectId;

  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateContentTypes: true,
    shouldUpdateFilters: true,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: fetchedAllData,
    resetStore: resetStore
  };

  const ProjectSpaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'project',
    subspaceName: undefined
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={ProjectSpaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ProjectQueryProvider>
          <ConnectedProjectSpaceProviderContainer {...{...props, renderProjectSpace, setFetchedAllData: setFetchedAllData}}/>
        </ProjectQueryProvider>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
});
